import { useEffect, useRef } from "react";
import "./homeMotion.css";

/** Short, progressive enhancements; never alter scroll distance or hide content. */
export default function useHomeMotion() {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !("IntersectionObserver" in window)) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const desktop = window.matchMedia("(min-width: 1024px) and (pointer: fine)");
    let cleanup = () => {};
    const setup = () => {
      cleanup();
      if (reduced.matches || navigator.connection?.saveData) return;
      const animations = new Set();
      const wide = desktop.matches;
      const hero = root.querySelector(":scope > section");
      const targets = [];
      root.querySelectorAll(":scope > section").forEach((section) => {
        if (section === hero) return;
        section.querySelectorAll("h2").forEach((el) => targets.push([el, "heading"]));
        section.querySelectorAll("img").forEach((el) => {
          if (el.clientWidth > 160 && el.clientHeight > 120) targets.push([el, "image"]);
        });
      });
      const kind = new Map(targets);
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          observer.unobserve(target);
          if (!target.animate || target.contains(document.activeElement)) return;
          const isImage = kind.get(target) === "image";
          const frames = isImage && wide
            ? [{ opacity: 0.55, clipPath: "inset(0 0 7% 0)", transform: "scale(1.025)" }, { opacity: 1, clipPath: "inset(0 0 0% 0)", transform: "scale(1)" }]
            : [{ opacity: 0.3, transform: `translateY(${wide ? 16 : 8}px)` }, { opacity: 1, transform: "translateY(0)" }];
          const animation = target.animate(frames, { duration: wide ? (isImage ? 560 : 440) : 320, easing: "cubic-bezier(0.22,1,0.36,1)", fill: "none" });
          animations.add(animation);
          animation.onfinish = () => animations.delete(animation);
        });
      }, { threshold: 0.08, rootMargin: "0px 0px -24px 0px" });
      targets.forEach(([el]) => {
        if (el.getBoundingClientRect().top >= window.innerHeight - 24) observer.observe(el);
      });
      let frame = 0;
      let previous = -1;
      const update = () => {
        frame = 0;
        const shift = Math.min(16, Math.max(0, window.scrollY * 0.035));
        if (shift !== previous) { root.style.setProperty("--home-hero-shift", `${shift}px`); previous = shift; }
      };
      const onScroll = () => {
        if (!frame && (window.scrollY < (hero?.offsetHeight || 900) || previous < 16)) frame = requestAnimationFrame(update);
      };
      const finishForFocus = () => animations.forEach((animation) => animation.finish());
      if (wide) { root.dataset.homeMotion = "desktop"; window.addEventListener("scroll", onScroll, { passive: true }); update(); }
      root.addEventListener("focusin", finishForFocus);
      cleanup = () => {
        observer.disconnect();
        window.removeEventListener("scroll", onScroll);
        root.removeEventListener("focusin", finishForFocus);
        cancelAnimationFrame(frame);
        animations.forEach((animation) => animation.cancel());
        delete root.dataset.homeMotion;
        root.style.removeProperty("--home-hero-shift");
      };
    };
    setup();
    reduced.addEventListener("change", setup);
    desktop.addEventListener("change", setup);
    return () => { cleanup(); reduced.removeEventListener("change", setup); desktop.removeEventListener("change", setup); };
  }, []);
  return rootRef;
}
